import * as XLSX from 'xlsx'
import { jsPDF } from 'jspdf'

/**
 * Prepara archivos (Excel + PDF único) para enviar a gestoría
 * @param {Array} facturas - Array de facturas a incluir
 * @param {Function} generarPDFFactura - Función que genera PDF de una factura
 * @returns {Object} - { excelBlob, pdfBlob, nombreExcel, nombrePdf }
 */
export async function prepararArchivosGestoria(facturas, generarPDFFactura) {
  console.log(`📦 Preparando archivos para ${facturas.length} facturas...`)
  
  try {
    // 1. Generar Excel con múltiples hojas
    console.log('📊 Generando Excel...')
    const excelBlob = await generarExcelMultihojas(facturas)
    
    // 2. Generar PDF único con todas las facturas
    console.log('📄 Generando PDF único...')
    const pdfBlob = await generarPDFUnico(facturas, generarPDFFactura)
    
    const fecha = new Date().toISOString().split('T')[0]
    
    console.log('✅ Excel generado:', excelBlob instanceof Blob, excelBlob?.size, 'bytes');
    console.log('✅ PDF generado:', pdfBlob instanceof Blob, pdfBlob?.size, 'bytes');

    return {
      excel: excelBlob,
      pdf: pdfBlob,
      nombreExcel: `facturas-${fecha}.xlsx`,
      nombrePdf: `facturas-${fecha}.pdf`
    }
  } catch (error) {
    console.error('❌ Error preparando archivos:', error)
    throw new Error(`Error preparando archivos para gestoría: ${error.message}`)
  }
}

/**
 * Genera Excel con una hoja por factura
 */
async function generarExcelMultihojas(facturas) {
  const workbook = XLSX.utils.book_new()
  
  facturas.forEach((factura, index) => {
    console.log(`  📄 Procesando factura ${index + 1}/${facturas.length}: ${factura.serie}-${factura.numero}`)
    
    // Datos de la factura
    const datos = [
      ['FACTURA', `${factura.serie}-${factura.numero}`],
      ['Cliente', factura.cliente_nombre || 'N/A'],
      ['CIF', factura.cliente_cif || 'N/A'],
      ['Fecha', factura.fecha_emision || 'N/A'],
      ['Período', `${factura.periodo_inicio} - ${factura.periodo_fin}`],
      [],
      ['LÍNEAS DE FACTURA'],
      ['Concepto', 'Cantidad', 'Precio Unit.', 'Base', 'IVA %', 'Total']
    ]
    
    // Agregar líneas
    if (factura.lineas && factura.lineas.length > 0) {
      factura.lineas.forEach(linea => {
        datos.push([
          linea.concepto || linea.descripcion || 'N/A',
          linea.cantidad || 0,
          linea.precio_unit || linea.precio_unitario || 0,
          linea.base || linea.subtotal || 0,
          linea.iva_pct || linea.iva_porcentaje || 0,
          linea.total || 0
        ])
      })
    } else {
      datos.push(['Sin líneas', '', '', '', '', ''])
    }
    
    // Totales — desglosados por tipo de IVA
    datos.push([])
    datos.push(['TOTALES'])

    const RECARGOS = { 4: 0.5, 10: 1.4, 21: 5.2 }

    const gruposIVA = {}
    if (factura.lineas && factura.lineas.length > 0) {
      factura.lineas.forEach(linea => {
        const pct = linea.iva_pct ?? linea.iva_porcentaje ?? 0
        const base = Number(linea.base ?? linea.subtotal ?? 0)
        if (!gruposIVA[pct]) gruposIVA[pct] = { base: 0, iva: 0, recargo: 0 }
        gruposIVA[pct].base += base
        gruposIVA[pct].iva += base * (pct / 100)
        gruposIVA[pct].recargo += base * ((RECARGOS[pct] || 0) / 100)
      })
    }

    const tiposOrdenados = Object.keys(gruposIVA).map(Number).sort((a, b) => a - b)

    if (tiposOrdenados.length > 1) {
      tiposOrdenados.forEach(pct => {
        const g = gruposIVA[pct]
        datos.push([`Base imponible ${pct}%`, '', '', '', '', Number(g.base.toFixed(2))])
        datos.push([`IVA ${pct}%`, '', '', '', '', Number(g.iva.toFixed(2))])
        if (g.recargo > 0) {
          datos.push([`Recargo Equiv. ${RECARGOS[pct] || ''}%`, '', '', '', '', Number(g.recargo.toFixed(2))])
        }
      })
    } else {
      const pct = tiposOrdenados[0]
      const g = gruposIVA[pct] || {}
      datos.push([`Base imponible ${pct ?? ''}%`, '', '', '', '', Number((g.base || 0).toFixed(2))])
      datos.push([`IVA ${pct ?? ''}%`, '', '', '', '', Number((g.iva || 0).toFixed(2))])
      if ((g.recargo || 0) > 0) {
        datos.push([`Recargo Equiv. ${RECARGOS[pct] || ''}%`, '', '', '', '', Number(g.recargo.toFixed(2))])
      }
    }

    datos.push(['TOTAL', '', '', '', '', factura.total || 0])
    
    // Crear hoja
    const worksheet = XLSX.utils.aoa_to_sheet(datos)
    
    // Nombre de hoja (max 31 chars)
    const nombreHoja = `${factura.serie}-${factura.numero}`.substring(0, 31)
    XLSX.utils.book_append_sheet(workbook, worksheet, nombreHoja)
  })
  
  // Generar blob
  const excelBuffer = XLSX.write(workbook, { type: 'array', bookType: 'xlsx' })
  return new Blob([excelBuffer], { 
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
  })
}

/**
 * Genera PDF único con todas las facturas
 * CORRECCIÓN: Maneja correctamente el tipo de retorno de generarPDFFactura
 */
async function generarPDFUnico(facturas, generarPDFFactura) {
  console.log('🔧 Iniciando generación de PDF único...')
  
  // Obtener configuración
  let configuracion = null
  try {
    const { supabase } = await import('../services/supabase')
    const { data } = await supabase
      .from('configuracion')
      .select('*')
      .limit(1)
      .single()
    configuracion = data
  } catch (error) {
    console.warn('⚠️ No se pudo cargar configuración, usando valores por defecto')
    configuracion = { nombre_empresa: 'Panadería Hermanos Alonso S.L.' }
  }
  
  // Crear PDF combinado
  let pdfCombinado = null
  
  for (let i = 0; i < facturas.length; i++) {
    const factura = facturas[i]
    console.log(`  📄 Agregando factura ${i + 1}/${facturas.length}: ${factura.serie}-${factura.numero}`)
    
    try {
      // CORRECCIÓN: Generar PDF y obtener el documento
      const doc = await generarPDFFactura(factura, configuracion)
      
      // CORRECCIÓN: Verificar que doc es un objeto jsPDF válido
      if (!doc || typeof doc.output !== 'function') {
        console.error(`❌ generarPDFFactura no devolvió un objeto jsPDF válido para ${factura.serie}-${factura.numero}`)
        throw new Error('PDF inválido generado')
      }
      
      if (i === 0) {
        // Primera factura: usar como base
        pdfCombinado = doc
      } else {
        // Facturas siguientes: agregar páginas
        const numPaginas = doc.internal.getNumberOfPages()
        
        for (let p = 1; p <= numPaginas; p++) {
          // Agregar nueva página
          pdfCombinado.addPage()
          
          // Copiar contenido de la página
          const paginaActual = pdfCombinado.internal.getCurrentPageInfo().pageNumber
          
          // Obtener el contenido de la página del documento fuente
          doc.setPage(p)
          const pageContent = doc.internal.pages[p]
          
          if (pageContent) {
            pdfCombinado.internal.pages[paginaActual] = pageContent
          }
        }
      }
      
      console.log(`  ✅ Factura ${factura.serie}-${factura.numero} agregada`)
      
    } catch (error) {
      console.error(`❌ Error agregando factura ${factura.serie}-${factura.numero}:`, error)
      // Continuar con las demás facturas
    }
  }
  
  if (!pdfCombinado) {
    throw new Error('No se pudo generar ningún PDF')
  }
  
  // CORRECCIÓN: Convertir a Blob correctamente
  console.log('💾 Convirtiendo PDF a Blob...')
  const pdfBlob = pdfCombinado.output('blob')
  
  console.log(`✅ PDF único generado (${facturas.length} facturas)`)
  return pdfBlob
}

/**
 * Alternativa: Generar PDFs individuales y combinarlos usando pdf-lib
 * (Más robusto pero requiere la librería pdf-lib)
 */
export async function generarPDFUnicoConPdfLib(facturas, generarPDFFactura) {
  try {
    // Importar pdf-lib dinámicamente
    const { PDFDocument } = await import('pdf-lib')
    
    const pdfCombinado = await PDFDocument.create()
    
    for (let i = 0; i < facturas.length; i++) {
      const factura = facturas[i]
      console.log(`  📄 Procesando ${i + 1}/${facturas.length}: ${factura.serie}-${factura.numero}`)
      
      try {
        // Generar PDF individual
        const doc = await generarPDFFactura(factura)
        
        // Convertir a ArrayBuffer
        const pdfBytes = doc.output('arraybuffer')
        
        // Cargar en pdf-lib
        const pdfDoc = await PDFDocument.load(pdfBytes)
        
        // Copiar todas las páginas
        const paginas = await pdfCombinado.copyPages(pdfDoc, pdfDoc.getPageIndices())
        paginas.forEach(pagina => pdfCombinado.addPage(pagina))
        
        console.log(`  ✅ Factura ${factura.serie}-${factura.numero} agregada`)
      } catch (error) {
        console.error(`❌ Error con factura ${factura.serie}-${factura.numero}:`, error)
      }
    }
    
    // Guardar PDF combinado
    const pdfBytes = await pdfCombinado.save()
    return new Blob([pdfBytes], { type: 'application/pdf' })
    
  } catch (error) {
    console.error('❌ Error generando PDF con pdf-lib:', error)
    throw error
  }
}

/**
 * Función auxiliar para verificar si un objeto es un jsPDF válido
 */
function esJsPDFValido(obj) {
  return obj && 
         typeof obj === 'object' && 
         typeof obj.output === 'function' &&
         typeof obj.addPage === 'function' &&
         obj.internal
}

export default {
  prepararArchivosGestoria,
  generarPDFUnicoConPdfLib
}